<!--template html-->
<template>
    <div v-if="message" class="gc-column-content message-editable dontsplit" :class="{ 'gc-no-image': !hasImage }" :data-id="message.UniqueId" :id="'msg-'+idKey+'-'+message.UniqueId" :data-message="message.MessageId">
        <div class="gc-column-outline">
            <div class="gc-column-text">
                <div class="gc-message-parent">
                    <div class="gc-message-photo-img" v-if="hasImage">
                        <img class="img-fluid" :class="imageClassName" @load="imageLoaded" @error="imageError" :src="message.ImageUrl" alt="Group Card Image" />
                        <!-- GTD-6588: CP contributors place stickers on the image preview box
                             (.cp-img-preview), so coords are % of the PHOTO, not the card.
                             The 1-column layout letterboxes the image inside the photo box, so
                             the inner frame contain-fits to the image's natural aspect ratio
                             (pure CSS — the frame stays correct when the columnizer clones the
                             message into generated pages, unlike measured pixel offsets). Being
                             inside the photo container also stops the columnizer duplicating
                             stickers on continuation pages, since split clones drop it. -->
                        <div v-if="stickers.length" class="gc-message-stickers" aria-hidden="true">
                            <div class="gc-message-sticker-frame" :style="photoFrameStyle">
                                <img v-for="s in stickers" :key="s.id" :src="s.url" alt=""
                                     class="gc-message-sticker" loading="lazy" decoding="async"
                                     :style="{ left: s.x + '%', top: s.y + '%', width: s.size + '%', transform: 'rotate(' + (s.rotation || 0) + 'deg)' }" />
                            </div>
                        </div>
                    </div>
                    <!-- GTD-6588: per-message text style (font/colour/size) comes from
                         GroupCardExtras and is applied here the same way stickers are.
                         Inline styles sit directly on the text nodes so they win over the
                         shared groupcard CSS (which hard-codes the font on span/p). -->
                    <div class="gc-message-body">
                        <!-- Message and sender name -->
                        <span class="gc-message-text" v-html="message.Message" :style="textStyle"></span>
                        <!-- GTD-0828: Platform's sender line has no text-align override (left/
                             default) and no "- " prefix; "text-right" here was a legacy holdover
                             from the classic Razor path. .gc-message-from (see gt-mt-groupcard.css)
                             matches Platform's .egc-msg-card__from (margin:0, bold, capitalize). -->
                        <p class="gc-message-from" :style="fromStyle">{{message.Name}}</p>
                    </div>
                </div>
            </div>
            <!-- gc-column-text -->
        </div>
        <!-- Fallback for sticker records on image-less messages (PlatForm-authored %,
             CP always requires an image): overlay the whole card box as before. -->
        <div v-if="!hasImage && stickers.length" class="gc-message-stickers" aria-hidden="true">
            <div class="gc-message-sticker-frame">
                <img v-for="s in stickers" :key="s.id" :src="s.url" alt=""
                     class="gc-message-sticker"
                     :style="{ left: s.x + '%', top: s.y + '%', width: s.size + '%', transform: 'rotate(' + (s.rotation || 0) + 'deg)' }" />
            </div>
        </div>
    </div>
    <!-- gc-column-content -->
</template>
<!--template html-->
<!--script -->
<script>
    import { messageTextStyle, messageFromStyle } from './messageStyle'

    export default {
        name: 'GroupCardMessageRawV2',
        props: {
            message: Object,
            index: Number,
            columnType: Number,
            idKey: String
        },
        data: function () {
            return {
                imageClassName: "",
                hasImage: false,
                photoNaturalSize: null
            }
        },
        computed: {
            // GTD-6588: parse StickersJson and normalize CP legacy pixel coords (any coord > 100)
            // by dividing by CP's sticker surface height (.cp-img-preview is a fixed 350px-tall
            // box the photo cover-fills). Records saved as % (all coords <= 100) pass through
            // unchanged so the same overlay works on any surface (edit, view, PDF).
            stickers() {
                const raw = this.message && this.message.StickersJson
                if (!raw) return []
                let parsed
                try { parsed = JSON.parse(raw) } catch (e) { return [] }
                if (!Array.isArray(parsed)) return []
                const CP_LEGACY_SURFACE_PX = 350
                return parsed.map(s => {
                    const isLegacyPx = (s.x || 0) > 100 || (s.y || 0) > 100 || (s.size || 0) > 100
                    if (!isLegacyPx) return s
                    return Object.assign({}, s, {
                        x: (s.x / CP_LEGACY_SURFACE_PX) * 100,
                        y: (s.y / CP_LEGACY_SURFACE_PX) * 100,
                        size: (s.size / CP_LEGACY_SURFACE_PX) * 100,
                    })
                })
            },
            // GTD-7466: mirror PlatForm's frame sizing — aspect-ratio pins the shape to
            // the photo and the min(100%, natural-px) caps contain-fit the frame WITHOUT
            // upscaling past a small image, matching the img element's own no-upscale
            // sizing. This keeps sticker % positions locked to the visible photo whether
            // the natural image is smaller or larger than the photo box.
            photoFrameStyle() {
                const n = this.photoNaturalSize
                if (!n) return null
                return {
                    aspectRatio: n.w + ' / ' + n.h,
                    width: 'auto',
                    height: 'auto',
                    maxWidth: 'min(100%, ' + n.w + 'px)',
                    maxHeight: 'min(100%, ' + n.h + 'px)',
                }
            },
            // GTD-6588: per-message font/colour/size from GroupCardExtras (StyleFont/StyleColor/
            // StyleSize). Null when the message carries no style, so the layout default is kept.
            textStyle() {
                return messageTextStyle(this.message)
            },
            fromStyle() {
                return messageFromStyle(this.message)
            }
        },
        mounted: function () {
            this.initMessage()
        },
        methods: {
            initMessage() {
                //checking if message has image
                if (this.message.ImageUrl) {
                    if (!(this.message.ImageUrl.indexOf("no_pic.jpg") > -1)) {
                        this.hasImage = true;
                    }
                }
                // If hasImage=true, imageLoaded will handle the emit when the img loads
                if (this.hasImage) return;
                // No ImageUrl - after Vue renders v-html content, wait for any embedded images
                this.$nextTick(() => this.reportWhenReady());
            },
            imageLoaded(event) {
                // GTD-7466: record the photo's natural px so the sticker frame can
                // contain-fit the visible image (see photoFrameStyle)
                if (event.target.naturalWidth > 0 && event.target.naturalHeight > 0) {
                    this.photoNaturalSize = { w: event.target.naturalWidth, h: event.target.naturalHeight }
                }
                if (this.columnType === 1) {
                    let image = event.target
                    if (image.naturalHeight > 0) {
                        //setting appropriate image class name for 1-column designs
                        if (this.imageClassName === "") {
                            if (((image.naturalWidth / image.naturalHeight) >= 1.5))
                                this.imageClassName = "gc-cover-image"
                            else
                                this.imageClassName = "gc-tile-image"
                        }
                    }
                } else if (this.columnType === 2) {
                    this.imageClassName = "gc-single-image-col-2"
                } else {
                    this.imageClassName = "gc-single-image-col-3"
                }
                // Wait for Vue to apply imageClassName to the DOM, then check all images
                // (including any embedded in the message text) before reporting height
                this.$nextTick(() => this.reportWhenReady());
            },
            // GTD-7755: a message image that fails to load (e.g. a user upload whose blob
            // now 404s) is REMOVED entirely rather than replaced with a placeholder, so the
            // recipient can tell the photo didn't attach. Previously the broken image left
            // naturalWidth=0, which stalled the print page's verifyImagesAndConvert poll and
            // timed out the PDF conversion; dropping the img avoids that and we still report
            // readiness so columnization / conversion proceeds.
            imageError() {
                this.hasImage = false
                this.imageClassName = ''
                if (!this._reported) this.$nextTick(() => this.reportWhenReady());
            },
            reportWhenReady() {
                if (this._reported) return;
                // Find all imgs in this component (ImageUrl img + any embedded in message HTML).
                // GTD-7466: sticker overlay images are decorative absolutely-positioned layers —
                // they don't affect message height, so waiting for them delays columnization
                // without changing the measured layout. Exclude them from the readiness gate.
                const allImgs = Array.from(this.$el.querySelectorAll('img'))
                    .filter(img => !img.closest('.gc-message-stickers'));
                const pending = allImgs.filter(img => !img.complete);
                if (pending.length === 0) {
                    // All images already loaded (or no images at all) — report immediately
                    this._reported = true;
                    this.$emit('update-parent', 1, this.message.UniqueId);
                    return;
                }
                if (this._waitingForImgs) return;
                this._waitingForImgs = true;
                // Wait for every pending image to finish loading or error before reporting
                let remaining = pending.length;
                const done = () => {
                    remaining--;
                    if (remaining <= 0 && !this._reported) {
                        this._reported = true;
                        this.$emit('update-parent', 1, this.message.UniqueId);
                    }
                };
                pending.forEach(img => {
                    img.addEventListener('load', done, { once: true });
                    img.addEventListener('error', done, { once: true });
                });
            }
        }
    }
</script>
<!--script -->
<!--Add "scoped" attribute to limit CSS to this component only-->
<style scoped>
    /* GTD-6588: sticker overlay sits on the photo box (.gc-message-photo-img) to mirror
       CP's authoring surface; for image-less messages it falls back to the card box
       (.gc-column-content, position:relative in the shared groupcard CSS). The inner
       frame contain-fits the image's aspect ratio (inline style) so stickers stay on
       the visible photo even when the 1-column layout letterboxes it; without the
       inline style it fills the overlay (photo box / card box). */
    .gc-message-photo-img {
        position: relative;
    }
    .gc-message-stickers {
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 3;
    }
    /* inset:0 + margin:auto centers the frame on both axes once the inline
       aspect-ratio + auto dims let the max constraints contain-fit it */
    .gc-message-sticker-frame {
        position: absolute;
        inset: 0;
        margin: auto;
        width: 100%;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
        overflow: hidden;
    }
    .gc-message-sticker {
        position: absolute;
        aspect-ratio: 1 / 1;
        height: auto;
        object-fit: contain;
        transform-origin: center;
    }
</style>
